
import React from "react";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import CollectionContextMenu from "@/components/common/CollectionContextMenu";
import ColorSquareIcon from "@/components/icons/ColorSquareIcon";
import { Collection } from "@/contexts/collections";

interface CollectionItemProps {
  collection: Collection;
  isActive: boolean;
  draggingId: string | null;
  dropTargetId: string | null;
  contentDropTargetId: string | null;
  onDragStart: (e: React.DragEvent, id: string) => void;
  onDragOver: (e: React.DragEvent, id: string) => void;
  onDragLeave: () => void;
  onDrop: (e: React.DragEvent, id: string) => void;
  onDragEnd: () => void;
  onContentDragOver: (e: React.DragEvent, id: string) => void;
  onContentDrop: (e: React.DragEvent, id: string) => void;
  onEdit: (id: string) => void;
  onDelete: (id: string) => void;
  onToggleVisibility: (id: string) => void;
}

const CollectionItem: React.FC<CollectionItemProps> = ({
  collection,
  isActive,
  draggingId,
  dropTargetId,
  contentDropTargetId,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onDragEnd,
  onContentDragOver,
  onContentDrop,
  onEdit,
  onDelete,
  onToggleVisibility
}) => {
  const getDroppableClass = (isActive: boolean, isContentDropTarget: boolean = false) => {
    if (isContentDropTarget) {
      return "bg-accent";
    }
    
    return cn("transition-colors duration-200", {
      "bg-accent": isActive,
      "hover:bg-accent/50": !isActive
    });
  };
  
  return (
    <div 
      className={cn("h-8 relative", {
        "opacity-50": draggingId === collection.id,
      })}
      draggable
      onDragStart={(e) => onDragStart(e, collection.id)}
      onDragOver={(e) => onDragOver(e, collection.id)}
      onDragLeave={onDragLeave}
      onDrop={(e) => onDrop(e, collection.id)}
      onDragEnd={onDragEnd}
    >
      {dropTargetId === collection.id && draggingId !== collection.id && (
        <div className="absolute top-0 left-0 right-0 h-0.5 bg-clustr-blue z-10" />
      )}
      
      <CollectionContextMenu
        collectionId={collection.id}
        collectionName={collection.name}
        collectionColor={collection.color || "#ccc"}
        isHidden={collection.hidden}
        onEdit={onEdit}
        onDelete={onDelete}
        onToggleVisibility={onToggleVisibility}
      >
        <div 
          className={cn(
            "flex items-center w-full px-3 py-0 h-8 transition-colors", 
            getDroppableClass(isActive),
            {
              "bg-accent": contentDropTargetId === collection.id
            }
          )}
          onDragOver={(e) => onContentDragOver(e, collection.id)}
          onDragLeave={onDragLeave}
          onDrop={(e) => onContentDrop(e, collection.id)}
        >
          <div className="h-5 w-5 mr-3 flex items-center justify-center cursor-grab">
            <ColorSquareIcon 
              size={18} 
              color={collection.color || "#ccc"} 
            />
          </div>
          <Link 
            to={`/collection/${collection.id}`} 
            className="flex items-center flex-1 h-full text-sm"
            data-testid={`collection-link-${collection.id}`}
            data-collection-name={collection.name}
          >
            <span 
              className={cn("truncate", {
                "line-through": collection.hidden
              })} 
              title={collection.hidden ? `${collection.name} (Hidden)` : collection.name}
            >
              {collection.name}
            </span>
          </Link>
        </div>
      </CollectionContextMenu>
    </div>
  );
};

export default CollectionItem;
